/**
 * Mobile Feature Flags
 *
 * Why this exists:
 *   Apple App Store guidelines (specifically 3.1.5(b) "Cryptocurrencies"
 *   and 3.1.1 "In-App Purchase") force us to selectively disable a few
 *   features when the React app is running inside the iOS Capacitor shell:
 *
 *     • CAUL top-ups via Stripe          → 3.1.5(b): buying tokens in-app
 *     • USDC / crypto cashouts           → 3.1.5(b): crypto trading flow
 *     • Stripe practitioner subscription → 3.1.1: replaced by Apple IAP on iOS
 *
 *   On Android we keep the restrictions lighter (Google Play Billing only
 *   requires IAP for digital subscriptions; crypto wallet UX is allowed).
 *
 *   On the open web (thecauldron.app) every flag is true — full experience.
 *
 * Usage:
 *   import { mobileFlags } from "../lib/mobileFeatureFlags";
 *   if (!mobileFlags.cryptoCashoutsEnabled) return <WebOnlyNotice/>;
 *
 * Lazy detection — calls Capacitor's runtime API once on first import.
 */

let _isNative = false;
let _platform = "web";
let _ready = false;

async function _detect() {
  if (_ready) return;
  try {
    const { Capacitor } = await import("@capacitor/core");
    _isNative = Capacitor?.isNativePlatform?.() === true;
    _platform = Capacitor?.getPlatform?.() || "web";
  } catch {
    /* Not running with Capacitor; treat as web. */
  }
  _ready = true;
}
// Kick off detection at module load so the first synchronous read works.
_detect();

export const mobileFlags = {
  get ready() { return _ready; },
  get isNative() { return _isNative; },
  get platform() { return _platform; },
  get isIOS() { return _isNative && _platform === "ios"; },
  get isAndroid() { return _isNative && _platform === "android"; },

  // ── Apple-restricted flows ────────────────────────────────────────
  /** Stripe-funded CAUL token top-ups: blocked on iOS (Apple 3.1.5b).  */
  get caulTopupsEnabled()      { return !this.isIOS; },
  /** USDC / XRP / on-chain cashouts: blocked on iOS (Apple 3.1.5b).   */
  get cryptoCashoutsEnabled()  { return !this.isIOS; },
  /** XRPL DEX / Xaman wallet flows: blocked on iOS (Apple 3.1.5b).    */
  get xrplFeaturesEnabled()    { return !this.isIOS; },

  // ── IAP routing ───────────────────────────────────────────────────
  /** Use Apple IAP instead of Stripe for the $49.99 subscription. */
  get useAppleIAP()            { return this.isIOS; },
  /** Use Google Play Billing instead of Stripe (future). */
  get useGooglePlayBilling()   { return this.isAndroid; },
  /** Show Stripe-based subscribe CTA (web only). */
  get useStripeSubscription()  { return !this.isNative; },
};

/**
 * For component code that needs to wait for Capacitor detection before
 * deciding what to render. Most call sites don't need this — `_detect()`
 * runs at module load and resolves within ~10ms.
 */
export async function waitForFlags() {
  await _detect();
  return mobileFlags;
}
